import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Award, Clock, CheckCircle, Target, Sparkles, Calendar } from 'lucide-react';
import { useModules } from '../contexts/ModuleContext';
import { useAuth } from '../contexts/AuthContext';
import { graphqlService } from '../services/graphqlService';

const Progress = ({ showBackButton = true }) => {
  const { getModulesForUser } = useModules();
  const { user } = useAuth();
  const [userProgress, setUserProgress] = useState({});
  const [assignedModules, setAssignedModules] = useState([]);
  const [scheduledModuleIds, setScheduledModuleIds] = useState([]);
  const [overallStats, setOverallStats] = useState({
    completedModules: 0,
    totalModules: 0,
    averageScore: 0,
    totalTimeSpent: 0
  });

  useEffect(() => {
    if (!user) {
      setAssignedModules([]);
      setScheduledModuleIds([]);
      return;
    }

    const loadModules = async () => {
      try {
        console.log('Progress - Loading modules for user:', user.email, 'Role:', user.role);
        const userModules = await getModulesForUser(user);
        console.log('Progress - Received modules:', userModules?.length || 0, userModules);
        setAssignedModules(Array.isArray(userModules) ? userModules : []);
        
        // Load schedules to identify which modules are scheduled
        try {
          const schedules = await graphqlService.getAllSchedules();
          
          // Helper function to check if user is assigned to a schedule
          const isUserAssignedToSchedule = (schedule) => {
            if (!schedule || !schedule.assignments) {
              return false;
            }
            
            const assignments = schedule.assignments;
            const userRole = (user.role || '').toLowerCase();
            const userEmail = (user.email || '').toLowerCase();
            const userDepartment = (user.department || '').toLowerCase();
            const userDesignation = user.designation ? user.designation.toLowerCase() : null;
            const userSpecialization = user.specialization ? user.specialization.toLowerCase() : null;
            
            // Check role assignment
            if (assignments.roles && assignments.roles.length > 0 && userRole) {
              const hasRoleMatch = assignments.roles.some(role => 
                (role || '').toLowerCase() === userRole
              );
              if (hasRoleMatch) return true;
            }
            
            // Check department assignment
            if (assignments.departments && assignments.departments.length > 0 && userDepartment) {
              const hasDeptMatch = assignments.departments.some(dept => 
                (dept || '').toLowerCase() === userDepartment
              );
              if (hasDeptMatch) return true;
            }
            
            // Check designation assignment
            if (assignments.designations && assignments.designations.length > 0 && userDesignation) {
              const hasDesignationMatch = assignments.designations.some(designation => 
                (designation || '').toLowerCase() === userDesignation
              );
              if (hasDesignationMatch) return true;
            }
            
            // Check specialization assignment
            if (assignments.specializations && assignments.specializations.length > 0 && userSpecialization) {
              const hasSpecializationMatch = assignments.specializations.some(specialization => 
                (specialization || '').toLowerCase() === userSpecialization
              );
              if (hasSpecializationMatch) return true;
            }
            
            // Check individual user assignment
            if (assignments.users && assignments.users.length > 0 && userEmail) {
              const hasUserMatch = assignments.users.some(email => 
                (email || '').toLowerCase() === userEmail
              );
              if (hasUserMatch) return true;
            }
            
            return false;
          };
          
          // Get module IDs from schedules where user is assigned
          const scheduledIds = schedules
            .filter(schedule => isUserAssignedToSchedule(schedule))
            .map(schedule => schedule.moduleId);
          
          console.log('Progress - Scheduled module IDs:', scheduledIds);
          setScheduledModuleIds(scheduledIds);
        } catch (error) {
          console.error('Error loading schedules:', error);
          setScheduledModuleIds([]);
        }
      } catch (error) {
        console.error('Error loading assigned modules:', error);
        setAssignedModules([]);
        setScheduledModuleIds([]);
      }
    };

    loadModules();
  }, [user, getModulesForUser]);

  useEffect(() => {
    if (!user || !user.email) return;

    const loadProgress = async () => {
      try {
        const progress = await graphqlService.getProgress(user.email);
        setUserProgress(progress || {});
        
        // Calculate overall statistics: Completed modules = modules that are both completed AND quiz passed
        const progressValues = progress ? Object.values(progress) : [];
        const completedModules = progressValues.filter(p => p.completed && p.quizCompleted).length;
        
        const quizScores = progressValues
          .filter(p => p.quizScore !== undefined)
          .map(p => p.quizScore);
        
        const averageScore = quizScores.length > 0 
          ? Math.round(quizScores.reduce((sum, score) => sum + score, 0) / quizScores.length)
          : 0;

        // Get progress stats from GraphQL
        const stats = await graphqlService.getProgressStats(user.email);
        
        // Calculate filtered modules count (scheduled or directly assigned)
        // This will be recalculated when assignedModules or scheduledModuleIds change
        // For now, we'll use a placeholder and update it in a separate effect
        console.log('Progress - Setting stats. Assigned modules:', assignedModules.length);
        
        setOverallStats({
          completedModules: stats.completedModules || completedModules,
          totalModules: 0, // Will be updated by filteredModules count
          averageScore: stats.averageScore || averageScore,
          totalTimeSpent: stats.totalTimeSpent || 0
        });
      } catch (error) {
        console.error('Error loading progress:', error);
      }
    };

    // Load progress even if no modules assigned yet, to update stats when modules are loaded
    loadProgress();
  }, [assignedModules, user]);

  const getModuleProgress = (moduleId) => {
    return userProgress[moduleId] || { completed: false, quizScore: 0, quizCompleted: false };
  };

  // Filter modules to show only scheduled or directly assigned modules
  const filteredModules = useMemo(() => {
    if (assignedModules.length === 0) return [];
    
    return assignedModules.filter((module) => {
      // Check if module is scheduled
      const isScheduled = scheduledModuleIds.includes(module.id);
      
      // Check if module is directly assigned to this specific user
      if (!module.assignments) {
        return isScheduled; // If no assignments, only show if scheduled
      }
      
      const assignments = module.assignments;
      const userRole = (user?.role || '').toLowerCase();
      const userEmail = (user?.email || '').toLowerCase();
      const userDepartment = (user?.department || '').toLowerCase();
      const userDesignation = user?.designation ? user.designation.toLowerCase() : null;
      const userSpecialization = user?.specialization ? user.specialization.toLowerCase() : null;
      
      // Check if user's role matches
      const roleMatch = assignments.roles && assignments.roles.length > 0 && userRole &&
        assignments.roles.some(role => (role || '').toLowerCase() === userRole);
      
      // Check if user's email matches
      const userMatch = assignments.users && assignments.users.length > 0 && userEmail &&
        assignments.users.some(email => (email || '').toLowerCase() === userEmail);
      
      // Check if user's department matches
      const deptMatch = assignments.departments && assignments.departments.length > 0 && userDepartment &&
        assignments.departments.some(dept => (dept || '').toLowerCase() === userDepartment);
      
      // Check if user's designation matches
      const designationMatch = assignments.designations && assignments.designations.length > 0 && userDesignation &&
        assignments.designations.some(designation => (designation || '').toLowerCase() === userDesignation);
      
      // Check if user's specialization matches
      const specializationMatch = assignments.specializations && assignments.specializations.length > 0 && userSpecialization &&
        assignments.specializations.some(specialization => (specialization || '').toLowerCase() === userSpecialization);
      
      // Module is directly assigned if any assignment matches the user
      const hasDirectAssignment = roleMatch || userMatch || deptMatch || designationMatch || specializationMatch;
      
      // Module must be either scheduled or directly assigned to this user
      return isScheduled || hasDirectAssignment;
    });
  }, [assignedModules, scheduledModuleIds, user]);

  // Update totalModules based on filtered modules count
  useEffect(() => {
    if (filteredModules.length > 0 || assignedModules.length === 0) {
      setOverallStats(prev => ({
        ...prev,
        totalModules: filteredModules.length
      }));
      console.log('Progress - Updated totalModules to:', filteredModules.length, 'filtered modules');
    }
  }, [filteredModules.length, assignedModules.length]);

  const getCompletionPercentage = () => {
    return Math.round((overallStats.completedModules / overallStats.totalModules) * 100);
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#059669';
    if (score >= 70) return '#f59e0b';
    return '#ef4444';
  };

  const getScoreLabel = (score) => {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Good';
    if (score >= 70) return 'Passing';
    return 'Needs Improvement';
  };

  return (
    <div className="container">
      <div className="progress-header">
        <Link to="/" className="btn btn-secondary back-to-dashboard">
          <ArrowLeft size={16} style={{ marginRight: '8px' }} />
          Back to Dashboard
        </Link>
        
        <h1 className="text-3xl font-bold" style={{ color: '#0477aa' }}>Your Progress</h1>
        <p className="text-lg text-gray-600">Track your learning journey and achievements</p>
      </div>

      <div className="progress-overview card" style={{ marginBottom: '2rem' }}>
        <h2 className="text-lg font-bold mb-2" style={{ color: '#0477aa' }}>Overall Progress</h2>
        <div className="progress-bar mb-2">
          <div 
            className="progress-fill" 
            style={{ width: `${getCompletionPercentage()}%` }}
          ></div>
        </div>
        <p className="text-sm text-gray-600">
          {overallStats.completedModules} of {overallStats.totalModules} assigned modules completed
        </p>
      </div>

      <div className="progress-overview">
        <div className="grid grid-3">
          <div className="card stat-card">
            <div className="stat-icon">
              <CheckCircle size={32} color="#059669" />
            </div>
            <div className="stat-content">
              <h3 className="text-2xl font-bold">{overallStats.completedModules}</h3>
              <p className="text-gray-600">Modules Completed</p>
            </div>
          </div>

          <div className="card stat-card">
            <div className="stat-icon">
              <Award size={32} color="#1e40af" />
            </div>
            <div className="stat-content">
              <h3 className="text-2xl font-bold">{overallStats.averageScore}%</h3>
              <p className="text-gray-600">Average Quiz Score</p>
            </div>
          </div>

          <div className="card stat-card">
            <div className="stat-icon">
              <Target size={32} color="#7c3aed" />
            </div>
            <div className="stat-content">
              <h3 className="text-2xl font-bold">{getCompletionPercentage()}%</h3>
              <p className="text-gray-600">Overall Progress</p>
            </div>
          </div>
        </div>
      </div>

      <div className="progress-details">
        <h2 className="text-2xl font-bold mb-6" style={{ color: '#0477aa' }}>Module Progress</h2>
        
        <div className="modules-progress">
          {filteredModules.length > 0 ? filteredModules.map((module) => {
              const progress = getModuleProgress(module.id);
              const isModuleCompleted = progress.completed;
              const isQuizCompleted = progress.quizCompleted;
              const quizScore = progress.quizScore || 0;
              
              // Determine status: Completed only if module AND quiz are completed
              const isFullyCompleted = isModuleCompleted && isQuizCompleted;
              const isInProgress = isModuleCompleted && !isQuizCompleted;

            return (
              <div key={module.id} className="card module-progress-card">
                <div className="module-progress-header">
                  <div className="module-info">
                    <div className="module-icon">
                      {React.isValidElement(module.icon) ? module.icon : null}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold" style={{ color: '#0477aa' }}>{module.title}</h3>
                      <p className="text-gray-600">{module.description}</p>
                      {module.tags && module.tags.length > 0 && (
                        <div className="module-tags" style={{ marginTop: '0.5rem' }}>
                          {module.tags.map(tag => (
                            <span key={tag} className="module-tag">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="module-status">
                    {isFullyCompleted ? (
                      <div className="status-badge completed">
                        <CheckCircle size={16} />
                        <span>Completed</span>
                      </div>
                    ) : isInProgress ? (
                      <div className="status-badge in-progress">
                        <Clock size={16} />
                        <span>In Progress</span>
                      </div>
                    ) : (
                      <div className="status-badge new">
                        <Sparkles size={16} />
                        <span>New</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="module-progress-details">
                  <>
                      <div className="progress-item">
                        <span className="progress-label">Module Status:</span>
                        <span className={`progress-value ${isFullyCompleted ? 'completed' : isInProgress ? 'pending' : 'pending'}`}>
                          {isFullyCompleted ? 'Completed' : isInProgress ? 'Module Completed - Quiz Pending' : 'Not Started'}
                        </span>
                      </div>

                      {isQuizCompleted && (
                        <div className="progress-item">
                          <span className="progress-label">Quiz Score:</span>
                          <span 
                            className="progress-value"
                            style={{ color: getScoreColor(quizScore) }}
                          >
                            {quizScore}% - {getScoreLabel(quizScore)}
                          </span>
                        </div>
                      )}

                      <div className="progress-item">
                        <span className="progress-label">Difficulty:</span>
                        <span className="progress-value">{module.difficulty}</span>
                      </div>

                      <div className="progress-item">
                        <span className="progress-label">Duration:</span>
                        <span className="progress-value">{module.duration} minutes</span>
                      </div>
                    </>
                </div>

                <div className="module-progress-actions">
                  <Link 
                    to={`/module/${module.id}`} 
                    className="btn btn-primary"
                  >
                    {isModuleCompleted ? 'Review Module' : 'Start Module'}
                  </Link>
                  
                  {isModuleCompleted && (
                    <Link 
                      to={`/quiz/${module.id}`} 
                      className="btn btn-secondary"
                    >
                      {isQuizCompleted ? 'Retake Quiz' : 'Take Quiz'}
                    </Link>
                  )}
                </div>
              </div>
            );
          }) : (
            <div className="card text-center py-12">
              <div className="text-gray-500 mb-4">
                <Award size={48} className="mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Modules In Progress or Completed</h3>
                <p>You don't have any modules currently in progress or completed. Start a module to see your progress here.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {overallStats.completedModules === overallStats.totalModules && (
        <div className="card completion-celebration">
          <div className="celebration-content">
            <Award size={64} color="#f59e0b" />
            <h2 className="text-3xl font-bold mb-4">Congratulations!</h2>
            <p className="text-lg text-gray-600 mb-6">
              You have completed all training modules! You are now certified in training fundamentals.
            </p>
            <div className="achievement-badge">
              <Award size={24} />
              <span>TruMerit Training Certified</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Progress;
